import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/lib/api';
import type { ManagedUser, UserStatus } from '@/mocks/users';
import defaultAvatar from '@/assets/proilpardefaut.png';

type FilterRole = 'all' | 'vendor' | 'organizer' | 'client';
type FilterStatus = 'all' | 'active' | 'suspended' | 'pending';
type ModalMode = 'create' | 'edit' | 'detail' | 'delete' | null;

const STATUS_LABELS: Record<UserStatus, { label: string; emoji: string; cls: string }> = {
  active: { label: 'Actif', emoji: '🟢', cls: 'bg-emerald-100 text-emerald-700' },
  suspended: { label: 'Suspendu', emoji: '🔴', cls: 'bg-red-100 text-red-700' },
  pending: { label: 'En attente', emoji: '🟡', cls: 'bg-gold-100 text-gold-dark' },
};

const DEFAULT_AVATAR = defaultAvatar;

type ApiUser = {
  id: number;
  name: string;
  email: string;
  avatar?: string | null;
  phone?: string | null;
  role: ManagedUser['role'];
  status: UserStatus;
  approval_email_sent_at?: string | null;
  paroisse?: string | null;
  diocese_prefere?: string | null;
  created_at: string;
  total_products?: number;
  total_events?: number;
  total_sales?: number;
  total_tickets_sold?: number;
  vendor_application?: VendorApplication | null;
  products?: VendorProduct[];
};

type VendorApplication = {
  shop_name?: string | null;
  location?: string | null;
  category?: string | null;
  bio?: string | null;
  submitted_at?: string | null;
  product_proof_images?: string[];
};

type VendorProduct = {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
  price?: number;
  status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string | null;
  image?: string | null;
  created_at?: string | null;
};

type ManagedUserWithDetails = ManagedUser & {
  paroisse?: string | null;
  diocese_prefere?: string | null;
  vendorApplication?: VendorApplication | null;
  products?: VendorProduct[];
};

type AdminUserUpdateResponse = {
  user: ApiUser;
  approval_email_sent?: boolean | null;
  approval_email_error?: string | null;
};

type AdminUsersResponse = {
  items: ApiUser[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
  };
  stats: {
    total: number;
    admins: number;
    vendors: number;
    organizers: number;
    clients: number;
    pending: number;
  };
};

type UserForm = {
  name: string;
  email: string;
  phone: string;
  role: ManagedUser['role'];
  status: UserStatus;
  password: string;
};

const emptyForm: UserForm = {
  name: '',
  email: '',
  phone: '',
  role: 'client',
  status: 'active',
  password: '',
};

const userLocationLabel = (user: ApiUser) => {
  if (user.role === 'vendor' || user.role === 'organizer') {
    return user.vendor_application?.location || user.diocese_prefere || user.paroisse || 'Non renseigné';
  }

  return user.paroisse || user.diocese_prefere || 'Non renseigné';
};

const userStructureLabel = (user: ApiUser) => {
  if (user.role === 'vendor') {
    return user.vendor_application?.shop_name || user.paroisse || 'Compte vendeur';
  }

  if (user.role === 'organizer') {
    return user.vendor_application?.shop_name || user.paroisse || 'Compte organisateur';
  }

  if (user.role === 'admin') {
    return 'Administration';
  }

  return 'Utilisateur mobile';
};

const mapApiUser = (user: ApiUser): ManagedUserWithDetails => ({
  id: String(user.id),
  name: user.name,
  email: user.email,
  phone: user.phone ?? '',
  role: user.role,
  status: user.status,
  approvalEmailSentAt: user.approval_email_sent_at ?? null,
  createdAt: user.created_at,
  avatar: user.avatar ?? DEFAULT_AVATAR,
  location: userLocationLabel(user),
  paroisse: user.paroisse ?? null,
  diocese_prefere: user.diocese_prefere ?? null,
  shopName: user.role === 'vendor' ? userStructureLabel(user) : undefined,
  structureName: user.role !== 'vendor' ? userStructureLabel(user) : undefined,
  totalProducts: user.total_products ?? 0,
  totalSales: user.total_sales ?? 0,
  totalEvents: user.total_events ?? 0,
  totalTicketsSold: user.total_tickets_sold ?? 0,
  vendorApplication: user.vendor_application ?? null,
  products: user.products ?? [],
});

const visibleUsersOnly = (users: ApiUser[]) => users.filter((user) => user.role !== 'admin');

export default function UsersManagement() {
  const [users, setUsers] = useState<ManagedUserWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState<FilterRole>('all');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<AdminUsersResponse['pagination']>({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: null,
    to: null,
  });
  const [stats, setStats] = useState<AdminUsersResponse['stats']>({
    total: 0,
    admins: 0,
    vendors: 0,
    organizers: 0,
    clients: 0,
    pending: 0,
  });
  const [modal, setModal] = useState<ModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<ManagedUserWithDetails | null>(null);
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  const loadUsers = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(pagination.per_page),
      });

      if (filterRole !== 'all') params.set('role', filterRole);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (search.trim()) params.set('search', search.trim());

      const response = await apiRequest<AdminUsersResponse>(`/admin/users?${params.toString()}`);
      const visibleUsers = visibleUsersOnly(response.items);
      setUsers(visibleUsers.map(mapApiUser));
      setPagination({
        ...response.pagination,
        total: response.stats.total - response.stats.admins,
        from: visibleUsers.length ? response.pagination.from : null,
        to: visibleUsers.length ? (response.pagination.from ?? 1) + visibleUsers.length - 1 : null,
      });
      setStats({
        ...response.stats,
        total: response.stats.total - response.stats.admins,
        admins: 0,
      });
      if (response.pagination.last_page > 0 && page > response.pagination.last_page) {
        setCurrentPage(response.pagination.last_page);
      }
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterRole, filterStatus, pagination.per_page, search]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadUsers(currentPage);
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [currentPage, loadUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, filterStatus, search]);

  const pageNumbers = useMemo(() => {
    const totalPages = pagination.last_page || 1;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + 4);
    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  }, [currentPage, pagination.last_page]);

  const openCreate = () => {
    setSelectedUser(null);
    setForm(emptyForm);
    setError('');
    setModal('create');
  };

  const openEdit = (user: ManagedUserWithDetails) => {
    setSelectedUser(user);
    setForm({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status,
      password: '',
    });
    setError('');
    setModal('edit');
  };

  const openDetail = (user: ManagedUserWithDetails) => {
    setSelectedUser(user);
    setModal('detail');
  };

  const openDelete = (user: ManagedUserWithDetails) => {
    setSelectedUser(user);
    setError('');
    setModal('delete');
  };

  const validate = () => {
    if (form.name.trim().length < 3) return 'Nom requis (min 3 caractères).';
    if (!/\S+@\S+\.\S+/.test(form.email)) return 'Email invalide.';
    if (modal === 'create' && form.password.trim().length < 8) return 'Mot de passe requis (min 8 caractères).';
    if (modal === 'edit' && form.password && form.password.trim().length < 8) return 'Mot de passe trop court.';
    return '';
  };

  const saveUser = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        status: form.status,
        ...(form.password ? { password: form.password } : {}),
      };

      if (modal === 'create') {
        await apiRequest('/admin/users', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        setActionMessage('Utilisateur ajouté avec succès.');
      } else if (modal === 'edit' && selectedUser) {
        await apiRequest(`/admin/users/${selectedUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        setActionMessage('Utilisateur mis à jour avec succès.');
      }

      setModal(null);
      await loadUsers(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async () => {
    if (!selectedUser) return;

    setSaving(true);
    setError('');

    try {
      await apiRequest(`/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
      });
      setActionMessage('Utilisateur supprimé avec succès.');
      setModal(null);
      await loadUsers(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  const updateUserStatus = async (user: ManagedUserWithDetails, status: UserStatus) => {
    setSaving(true);
    setError('');

    try {
      const response = await apiRequest<AdminUserUpdateResponse>(`/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          phone: user.phone,
          paroisse: user.paroisse ?? user.location,
          diocese_prefere: user.diocese_prefere ?? user.vendorApplication?.location ?? user.location,
          role: user.role,
          status,
        }),
      });
      if (status === 'active') {
        setActionMessage(
          response.approval_email_sent === false
            ? `Compte de ${user.name} approuvé, mais l'email n'a pas pu etre envoye: ${response.approval_email_error ?? 'erreur inconnue'}.`
            : `Compte de ${user.name} approuvé. Email de confirmation envoyé.`
        );
      } else {
        setActionMessage(`Demande de ${user.name} rejetée.`);
      }
      await loadUsers(currentPage);
      setModal(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de mettre à jour le statut.');
    } finally {
      setSaving(false);
    }
  };

  const resendApprovalEmail = async (user: ManagedUserWithDetails) => {
    setSaving(true);
    setError('');

    try {
      const response = await apiRequest<AdminUserUpdateResponse>(`/admin/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: user.name,
          email: user.email,
          phone: user.phone,
          role: user.role,
          status: user.status,
          resend_approval_email: true,
        }),
      });

      setActionMessage(
        response.approval_email_sent === false
          ? `Le renvoi a échoué pour ${user.name}: ${response.approval_email_error ?? 'erreur inconnue'}.`
          : `Email d'approbation renvoyé à ${user.name}.`
      );
      await loadUsers(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de renvoyer l'email d'approbation.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div className="space-y-5">
      {actionMessage ? (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
          {actionMessage}
        </div>
      ) : null}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Total utilisateurs', value: stats.total, icon: 'ri-team-line', color: 'from-navy to-navy-light' },
          { label: 'Vendeurs', value: stats.vendors, icon: 'ri-store-2-line', color: 'from-emerald-500 to-teal-600' },
          { label: 'Organisateurs', value: stats.organizers, icon: 'ri-calendar-event-line', color: 'from-gold to-gold-dark' },
          { label: 'Utilisateurs mobile', value: stats.clients, icon: 'ri-smartphone-line', color: 'from-blue-500 to-indigo-600' },
          { label: 'Demandes en attente', value: stats.pending, icon: 'ri-hourglass-line', color: 'from-amber-400 to-orange-500' },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${item.color} flex-shrink-0`}>
              <i className={`${item.icon} text-white text-lg`}></i>
            </div>
            <div>
              <p className="text-xl font-bold text-navy">{item.value}</p>
              <p className="text-xs text-gray-500">{item.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg flex-wrap">
              {(['all', 'vendor', 'organizer', 'client'] as FilterRole[]).map((role) => (
                <button
                  key={role}
                  onClick={() => setFilterRole(role)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterRole === role ? 'bg-navy text-white' : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  {role === 'all' ? 'Tous' : role === 'client' ? 'Utilisateurs' : role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              ))}
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold/40"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="pending">En attente</option>
              <option value="suspended">Suspendus</option>
            </select>
          </div>

          <div className="flex gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-56">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold"
              />
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-semibold hover:bg-navy-light transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              Ajouter utilisateur
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Utilisateur</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Rôle</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Source / Structure</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Statut</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Depuis</th>
                <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">Chargement des utilisateurs...</td></tr>}
              {!loading && users.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400 text-sm">Aucun utilisateur trouvé</td></tr>}
              {users.map((user) => {
                const status = STATUS_LABELS[user.status];
                const roleColor =
                  user.role === 'vendor'
                    ? 'bg-emerald-100 text-emerald-700'
                    : user.role === 'organizer'
                      ? 'bg-gold-100 text-gold-dark'
                      : user.role === 'admin'
                        ? 'bg-slate-100 text-slate-700'
                        : 'bg-blue-100 text-blue-700';
                const roleIcon =
                  user.role === 'vendor'
                    ? 'ri-store-2-line'
                    : user.role === 'organizer'
                      ? 'ri-calendar-event-line'
                      : user.role === 'admin'
                        ? 'ri-shield-user-line'
                        : 'ri-smartphone-line';
                const source =
                  user.role === 'vendor'
                    ? user.shopName || 'Compte vendeur'
                    : user.role === 'organizer'
                      ? user.structureName || 'Compte organisateur'
                      : user.role === 'admin'
                        ? 'Administration'
                      : 'Inscription mobile';
                const approvalEmailSent = Boolean(user.approvalEmailSentAt);

                return (
                  <tr key={user.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover bg-gray-100 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-navy text-sm">{user.name}</p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${roleColor}`}>
                        <i className={roleIcon}></i>
                        {user.role === 'client' ? 'Utilisateur' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-sm text-gray-700 font-medium">{source}</p>
                      <p className="text-xs text-gray-400">{user.location}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${status.cls}`}>
                        {status.emoji} {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-xs text-gray-500">{formatDate(user.createdAt)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1 justify-end">
                        {user.status === 'pending' && (user.role === 'vendor' || user.role === 'organizer') ? (
                          <>
                            <button
                              onClick={() => void updateUserStatus(user, 'active')}
                              className="inline-flex h-8 items-center justify-center rounded-lg bg-emerald-50 px-3 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100"
                              title="Approuver l'inscription"
                            >
                              Approuver
                            </button>
                            <button
                              onClick={() => void updateUserStatus(user, 'suspended')}
                              className="inline-flex h-8 items-center justify-center rounded-lg bg-red-50 px-3 text-xs font-bold text-red-600 transition hover:bg-red-100"
                              title="Rejeter l'inscription"
                            >
                              Rejeter
                            </button>
                          </>
                        ) : null}
                        {user.status === 'active' && (user.role === 'vendor' || user.role === 'organizer') ? (
                          <button
                            onClick={() => void resendApprovalEmail(user)}
                            disabled={approvalEmailSent || saving}
                            className={`inline-flex h-8 items-center justify-center rounded-lg px-3 text-xs font-bold transition ${
                              approvalEmailSent || saving
                                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                            }`}
                            title={approvalEmailSent ? "Email d'approbation deja envoye" : "Renvoyer l'email d'approbation"}
                          >
                            Renvoyer email
                          </button>
                        ) : null}
                        <button onClick={() => openDetail(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-navy transition-all cursor-pointer" title="Détails">
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                        <button onClick={() => openEdit(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gold-100 text-gray-500 hover:text-gold-dark transition-all cursor-pointer" title="Modifier">
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button onClick={() => openDelete(user)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-100 text-gray-500 hover:text-red-600 transition-all cursor-pointer" title="Supprimer">
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-medium text-gray-500">
            {pagination.total > 0
              ? `${pagination.from ?? 0}-${pagination.to ?? 0} sur ${pagination.total} utilisateur${pagination.total > 1 ? 's' : ''}`
              : '0 utilisateur'}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={loading || currentPage <= 1}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              title="Page précédente"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            {pageNumbers.map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                disabled={loading || page === currentPage}
                className={`inline-flex h-9 min-w-9 items-center justify-center rounded-lg px-3 text-xs font-bold transition ${
                  page === currentPage
                    ? 'bg-navy text-white'
                    : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((page) => Math.min(pagination.last_page || 1, page + 1))}
              disabled={loading || currentPage >= (pagination.last_page || 1)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              title="Page suivante"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {(modal === 'create' || modal === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-navy">{modal === 'create' ? 'Ajouter utilisateur' : 'Modifier utilisateur'}</h3>
              <button onClick={() => setModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} placeholder="Nom complet" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <input value={form.email} onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))} placeholder="Email" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input value={form.phone} onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))} placeholder="Téléphone" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <input value={form.password} onChange={(e) => setForm((current) => ({ ...current, password: e.target.value }))} placeholder={modal === 'create' ? 'Mot de passe' : 'Nouveau mot de passe (optionnel)'} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={form.role} onChange={(e) => setForm((current) => ({ ...current, role: e.target.value as ManagedUser['role'] }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40">
                  <option value="client">Utilisateur mobile</option>
                  <option value="vendor">Vendeur</option>
                  <option value="organizer">Organisateur</option>
                </select>
                <select value={form.status} onChange={(e) => setForm((current) => ({ ...current, status: e.target.value as UserStatus }))} className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gold/40">
                  <option value="active">Actif</option>
                  <option value="pending">En attente</option>
                  <option value="suspended">Suspendu</option>
                </select>
              </div>
              {error ? <p className="text-sm text-red-500">{error}</p> : null}
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void saveUser()} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-navy text-white text-sm font-bold hover:bg-navy-light transition-all cursor-pointer whitespace-nowrap">
                {saving ? 'Enregistrement...' : modal === 'create' ? 'Ajouter' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'detail' && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-bold text-navy">Détails du compte</h3>
              <button onClick={() => setModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="max-h-[calc(90vh-132px)] overflow-y-auto p-6">
              <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="space-y-3">
                  <div className="flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <img src={selectedUser.avatar} alt={selectedUser.name} className="h-16 w-16 rounded-full object-cover" />
                    <div>
                      <h4 className="text-base font-bold text-navy">{selectedUser.name}</h4>
                      <p className="text-sm text-gray-500">{selectedUser.email}</p>
                      <p className="mt-1 text-xs font-semibold text-gray-400">Inscrit le {formatDate(selectedUser.createdAt)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Téléphone</p><p className="text-sm font-semibold text-navy">{selectedUser.phone || 'Non renseigné'}</p></div>
                    <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Rôle</p><p className="text-sm font-semibold text-navy">{selectedUser.role === 'vendor' ? 'Vendeur' : selectedUser.role}</p></div>
                    <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Statut</p><p className="text-sm font-semibold text-navy">{STATUS_LABELS[selectedUser.status].label}</p></div>
                    <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Localisation</p><p className="text-sm font-semibold text-navy">{selectedUser.location}</p></div>
                    <div className="rounded-xl bg-gray-50 p-3 sm:col-span-2"><p className="text-xs text-gray-400">Structure / boutique</p><p className="text-sm font-semibold text-navy">{selectedUser.vendorApplication?.shop_name || selectedUser.shopName || selectedUser.structureName || 'Non renseigné'}</p></div>
                    {selectedUser.role === 'vendor' ? (
                      <>
                        <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Catégorie</p><p className="text-sm font-semibold text-navy">{selectedUser.vendorApplication?.category || 'Non renseignée'}</p></div>
                        <div className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">Produits créés</p><p className="text-sm font-semibold text-navy">{selectedUser.totalProducts ?? 0}</p></div>
                      </>
                    ) : null}
                  </div>
                  {selectedUser.vendorApplication?.bio ? (
                    <div className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs text-gray-400">Présentation</p>
                      <p className="mt-1 text-sm font-medium leading-relaxed text-navy">{selectedUser.vendorApplication.bio}</p>
                    </div>
                  ) : null}
                </div>

                <div className="space-y-5">
                  <section>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-bold text-navy">Images fournies à l’inscription</h4>
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                        {selectedUser.vendorApplication?.product_proof_images?.length ?? 0} image(s)
                      </span>
                    </div>
                    {selectedUser.vendorApplication?.product_proof_images?.length ? (
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {selectedUser.vendorApplication.product_proof_images.map((image, index) => (
                          <button
                            key={`${image}-${index}`}
                            type="button"
                            onClick={() => setPreviewImage({ url: image, title: `Image fournie ${index + 1}` })}
                            className="group overflow-hidden rounded-xl border border-gray-100 bg-gray-50"
                          >
                            <img src={image} alt={`Produit fourni ${index + 1}`} className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-5 text-center text-sm font-medium text-gray-400">
                        Aucune image de candidature enregistrée.
                      </div>
                    )}
                  </section>

                  <section>
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-bold text-navy">Produits uploadés</h4>
                      <span className="rounded-full bg-gold-100 px-2.5 py-1 text-xs font-bold text-gold-dark">
                        {selectedUser.products?.length ?? 0} produit(s)
                      </span>
                    </div>
                    {selectedUser.products?.length ? (
                      <div className="space-y-3">
                        {selectedUser.products.map((product) => (
                          <div key={product.id} className="flex gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
                            <img src={product.image || DEFAULT_AVATAR} alt={product.name} className="h-20 w-20 flex-shrink-0 rounded-lg bg-gray-100 object-cover" />
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                  <p className="font-bold text-navy">{product.name}</p>
                                  <p className="text-xs text-gray-400">{product.category || 'Catégorie non renseignée'}</p>
                                </div>
                                <span className={`rounded-full px-2 py-1 text-xs font-bold ${
                                  product.status === 'approved'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : product.status === 'rejected'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {product.status === 'approved' ? 'Approuvé' : product.status === 'rejected' ? 'Rejeté' : 'En attente'}
                                </span>
                              </div>
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">{product.description || 'Aucune description.'}</p>
                              <p className="mt-2 text-sm font-bold text-gold-dark">{(product.price ?? 0).toLocaleString('fr-FR')} FCFA</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-5 text-center text-sm font-medium text-gray-400">
                        Aucun produit créé pour ce vendeur.
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex flex-col gap-3 sm:flex-row">
                {selectedUser.status === 'pending' && selectedUser.role === 'vendor' ? (
                  <>
                    <button onClick={() => void updateUserStatus(selectedUser, 'suspended')} disabled={saving} className="flex-1 rounded-xl border border-red-200 py-2.5 text-sm font-bold text-red-600 transition-all hover:bg-red-50 disabled:opacity-50">Rejeter</button>
                    <button onClick={() => void updateUserStatus(selectedUser, 'active')} disabled={saving} className="flex-1 rounded-xl bg-emerald-600 py-2.5 text-sm font-bold text-white transition-all hover:bg-emerald-700 disabled:opacity-50">Approuver le vendeur</button>
                  </>
                ) : (
                  <button onClick={() => setModal(null)} className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">Fermer</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewImage ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative max-h-[92vh] w-full max-w-4xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-navy shadow-lg transition hover:bg-white"
              title="Fermer l'image"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div className="border-b border-gray-100 px-4 py-3">
                <p className="text-sm font-bold text-navy">{previewImage.title}</p>
              </div>
              <img src={previewImage.url} alt={previewImage.title} className="max-h-[82vh] w-full object-contain" />
            </div>
          </div>
        </div>
      ) : null}

      {modal === 'delete' && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold text-navy mb-2">Supprimer ce compte ?</h3>
            <p className="text-sm text-gray-500 mb-4">Le compte de <strong>{selectedUser.name}</strong> sera supprimé définitivement.</p>
            {error ? <p className="text-sm text-red-500 mb-3">{error}</p> : null}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={() => void deleteUser()} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap">
                {saving ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
